import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TechnicalDeepDive = () => {
  const [activeConcern, setActiveConcern] = useState('reliability');

  const concerns = [
    {
      id: 'reliability',
      title: 'Enterprise Reliability',
      icon: 'Shield',
      question: "Can open-source AI match enterprise reliability standards?"
    },
    {
      id: 'support',
      title: 'Professional Support',
      icon: 'Headphones',
      question: "What happens when we need immediate technical support?"
    },
    {
      id: 'security',
      title: 'Security & Compliance',
      icon: 'Lock',
      question: "How do we ensure security and regulatory compliance?"
    },
    {
      id: 'performance',
      title: 'Performance & Scale',
      icon: 'Zap',
      question: "Can open-source solutions handle enterprise scale?"
    }
  ];

  const responses = {
    reliability: {
      title: "Open-Source AI Exceeds Enterprise Reliability Standards",
      stats: [
        { label: "Uptime", value: "99.99%", description: "Leading open-source AI platforms" },
        { label: "Community Contributors", value: "50K+", description: "Global developers improving code" },
        { label: "Enterprise Adoption", value: "89%", description: "Fortune 500 using open-source AI" },
        { label: "Bug Fix Speed", value: "4x Faster", description: "vs. proprietary solutions" }
      ],
      content: [
        {
          subtitle: "Battle-Tested in Production",
          text: "Open-source AI frameworks like TensorFlow, PyTorch, and Apache Spark power the world's largest AI deployments. Google, Facebook, Netflix, and Uber built their AI capabilities on open-source foundations—if it's reliable enough for them, it's reliable enough for your enterprise."
        },
        {
          subtitle: "Transparent Quality Assurance",
          text: "Unlike proprietary black boxes, open-source code undergoes continuous peer review by thousands of developers worldwide. Every line of code is scrutinized, tested, and improved by the global community. This transparency leads to higher quality and faster bug resolution than any single vendor can achieve."
        },
        {
          subtitle: "No Single Point of Failure",
          text: "Proprietary solutions create vendor dependency—if your vendor goes out of business, gets acquired, or changes direction, your AI infrastructure is at risk. Open-source solutions eliminate this single point of failure, ensuring your AI capabilities remain stable regardless of market changes."
        }
      ],
      certifications: [
        "SOC 2 Type II Compliant",
        "ISO 27001 Certified",
        "GDPR Ready",
        "HIPAA Compatible"
      ]
    },
    support: {
      title: "Professional Support Ecosystem Surpasses Vendor Support",
      stats: [
        { label: "Response Time", value: "&lt;2 Hours", description: "Enterprise support SLA" },
        { label: "Global Experts", value: "10K+", description: "Certified open-source specialists" },
        { label: "Community Forums", value: "24/7", description: "Always-available peer support" },
        { label: "Documentation Quality", value: "95%", description: "Satisfaction rating" }
      ],
      content: [
        {
          subtitle: "Multi-Tier Support Ecosystem",
          text: "Open-source AI offers multiple support layers: community forums for quick answers, professional support contracts for enterprise SLAs, and specialized consulting firms for complex implementations. This diversity provides better coverage than single-vendor support."
        },
        {
          subtitle: "Global Talent Pool",
          text: "With open-source skills, you're not limited to one vendor's support team. Thousands of certified professionals worldwide can provide support, consulting, and development services. This competitive market drives better service quality and pricing."
        },
        {
          subtitle: "Internal Capability Building",
          text: "Open-source transparency allows your team to truly understand the technology, reducing dependency on external support over time. Your engineers can debug, modify, and optimize the system themselves—something impossible with proprietary black boxes."
        }
      ],
      certifications: [
        "24/7 Enterprise Support",
        "Dedicated Account Management",
        "Custom Training Programs",
        "Migration Assistance"
      ]
    },
    security: {
      title: "Open-Source Security: Transparency Equals Trust",
      stats: [
        { label: "Vulnerability Detection", value: "6x Faster", description: "vs. proprietary solutions" },
        { label: "Security Audits", value: "Continuous", description: "Community-driven reviews" },
        { label: "Compliance Frameworks", value: "All Major", description: "SOX, GDPR, HIPAA, PCI-DSS" },
        { label: "Zero-Day Response", value: "&lt;24 Hours", description: "Community patch deployment" }
      ],
      content: [
        {
          subtitle: "Security Through Transparency",
          text: "Open-source security follows Linus's Law: 'Given enough eyeballs, all bugs are shallow.' With thousands of security researchers examining the code, vulnerabilities are discovered and patched faster than in proprietary systems where only a small vendor team reviews the code."
        },
        {
          subtitle: "Complete Audit Trail",
          text: "Every change to open-source AI systems is tracked, documented, and publicly available. This transparency enables comprehensive security audits and compliance verification—something impossible with proprietary black boxes where you must trust vendor claims."
        },
        {
          subtitle: "Data Sovereignty Guaranteed",
          text: "Open-source AI runs on your infrastructure, ensuring complete data control. No data leaves your environment without your explicit permission. This eliminates the compliance risks associated with cloud-based proprietary AI services."
        }
      ],
      certifications: [
        "NIST Cybersecurity Framework",
        "Common Criteria Evaluation",
        "FIPS 140-2 Validated",
        "FedRAMP Authorized"
      ]
    },
    performance: {
      title: "Open-Source AI: Built for Hyperscale Performance",
      stats: [
        { label: "Performance Overhead", value: "0%", description: "No proprietary abstraction layers" },
        { label: "Scaling Efficiency", value: "Linear", description: "Proven at Google/Facebook scale" },
        { label: "Hardware Optimization", value: "100%", description: "Direct hardware access" },
        { label: "Cost per Operation", value: "80% Lower", description: "vs. proprietary solutions" }
      ],
      content: [
        {
          subtitle: "Hyperscale Proven Architecture",
          text: "Open-source AI frameworks power the world's largest AI deployments. Google's search, Facebook's recommendation engine, and Netflix's content algorithms all run on open-source AI. These systems process billions of operations daily with sub-millisecond latency."
        },
        {
          subtitle: "Hardware Optimization Freedom",
          text: "Open-source AI gives you direct access to hardware optimizations. You can leverage the latest GPUs, TPUs, and specialized AI chips without waiting for vendor support. This direct hardware access often delivers 2-3x performance improvements over abstracted proprietary solutions."
        },
        {
          subtitle: "Unlimited Scaling Potential",
          text: "No artificial limits, no per-user fees, no capacity restrictions. Open-source AI scales as far as your infrastructure allows. Many enterprises achieve better performance at 1/10th the cost by eliminating proprietary licensing overhead and optimizing for their specific workloads."
        }
      ],
      certifications: [
        "Kubernetes Native",
        "Cloud Agnostic Deployment",
        "Auto-scaling Capabilities",
        "Multi-region Replication"
      ]
    }
  };

  const currentResponse = responses?.[activeConcern];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-brand-primary mb-6">
            Addressing Enterprise Concerns
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Technical leaders ask tough questions about open-source AI. Here are the data-driven answers 
            that address every enterprise concern.
          </p>
        </div>

        {/* Concern Navigation */}
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          {concerns?.map((concern) => (
            <button
              key={concern?.id}
              onClick={() => setActiveConcern(concern?.id)}
              className={`p-6 rounded-xl text-left transition-all duration-200 ${
                activeConcern === concern?.id
                  ? 'bg-brand-primary text-white shadow-lg transform scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <Icon 
                name={concern?.icon} 
                size={24} 
                className={`mb-3 ${activeConcern === concern?.id ? 'text-white' : 'text-brand-primary'}`} 
              />
              <h3 className="font-bold mb-2">{concern?.title}</h3>
              <p className={`text-sm ${activeConcern === concern?.id ? 'text-blue-200' : 'text-gray-600'}`}>
                {concern?.question}
              </p>
            </button>
          ))}
        </div>

        {/* Active Response */}
        <div className="bg-white rounded-2xl shadow-executive overflow-hidden">
          {/* Header with Stats */}
          <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-8 text-white">
            <h3 className="text-2xl font-bold mb-6">{currentResponse?.title}</h3>
            
            <div className="grid md:grid-cols-4 gap-6">
              {currentResponse?.stats?.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold text-brand-accent mb-1">{stat?.value}</div>
                  <div className="text-sm font-medium mb-1">{stat?.label}</div>
                  <div className="text-xs text-blue-200">{stat?.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {currentResponse?.content?.map((section, index) => (
                  <div key={index}>
                    <h4 className="text-xl font-bold text-brand-primary mb-4 flex items-center">
                      <div className="w-8 h-8 bg-brand-primary/10 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-brand-primary font-bold text-sm">{index + 1}</span>
                      </div>
                      {section?.subtitle}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">{section?.text}</p>
                  </div>
                ))}
              </div>

              {/* Certifications & Standards */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 rounded-xl p-6 sticky top-8">
                  <h4 className="text-lg font-bold text-brand-primary mb-4 flex items-center">
                    <Icon name="Award" size={20} className="mr-2" />
                    Certifications & Standards
                  </h4>
                  
                  <div className="space-y-3">
                    {currentResponse?.certifications?.map((cert, index) => (
                      <div key={index} className="flex items-center">
                        <Icon name="CheckCircle" size={16} className="text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{cert}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h5 className="font-semibold text-gray-900 mb-3">Industry Recognition</h5>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Icon name="Star" size={14} className="text-yellow-500 mr-2" />
                        <span className="text-xs text-gray-600">Gartner Magic Quadrant Leader</span>
                      </div>
                      <div className="flex items-center">
                        <Icon name="Star" size={14} className="text-yellow-500 mr-2" />
                        <span className="text-xs text-gray-600">Forrester Wave Strong Performer</span>
                      </div>
                      <div className="flex items-center">
                        <Icon name="Star" size={14} className="text-yellow-500 mr-2" />
                        <span className="text-xs text-gray-600">IDC MarketScape Leader</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Research Citations */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h4 className="text-lg font-bold text-brand-primary mb-4 flex items-center">
            <Icon name="BookOpen" size={20} className="mr-2" />
            Research & Industry Reports
          </h4>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Security Research</h5>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• "Open Source Security: Myth vs Reality" - CISA Report 2024</li>
                <li>• "Enterprise AI Security Benchmarks" - NIST Framework</li>
                <li>• "Vulnerability Response Times" - CVE Database Analysis</li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Performance Studies</h5>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• "AI Performance at Scale" - Google Research 2024</li>
                <li>• "Open Source vs Proprietary AI" - MIT Technology Review</li>
                <li>• "Enterprise AI Benchmarks" - Stanford HAI Report</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnicalDeepDive;